import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { reportsSlice } from '../../stores/reports-store';
import { setApplicationError } from '../../stores/app-state-store';
import { ResponseObj } from '../../lib-api/services/base';
import { ServiceDispatch } from '../../services/types';
import {
  AccountDialogType,
  GeneratedReport,
  GenerateReportAsyncResponse,
  GenerateReportResponse,
  GenerationJob,
  ReportAccount,
  ReportAccountsResponse,
  ReportType,
} from '../models/report';
import { RefreshableItem } from '../../models/refreshableItem';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { UNEXPECTED_ERROR } from '../../components/ApplicationErrorBoundary/ApplicationErrorBoundary';
import {
  BundledFlowParameters,
  GenerateReportAsyncPayload,
  generateReport as generateReportImport,
  GenerateReportPayload,
  generateReportsAsync as generateReportsAsyncImport,
  getAccounts as getAccountsImport,
} from './reports-client';

export interface ReportsClient {
  getAccounts: (payload: BundledFlowParameters) => Promise<ResponseObj<ReportAccountsResponse>>;
  generateReports: (
    payload: GenerateReportPayload,
    idempotencyKey: string
  ) => Promise<ResponseObj<GenerateReportResponse>>;
  generateReportsAsync: (
    payload: GenerateReportAsyncPayload,
    idempotencyKey: string
  ) => Promise<ResponseObj<GenerateReportAsyncResponse>>;
}

const reportsClient: ReportsClient = {
  getAccounts: getAccountsImport,
  generateReports: generateReportImport,
  generateReportsAsync: generateReportsAsyncImport,
};

export type ReportsDispatch = ServiceDispatch<typeof reportsSlice.actions>;

type ReportsThunk = (dispatch: ReportsDispatch) => Promise<void>;

export type GenerateReportsPayload = {
  accounts: string[] | undefined;
  reportTypes: ReportType[];
  customerReferenceId: string | undefined;
  externalReference: string | undefined;
  refreshableItems: RefreshableItem[];
  accountDialogType: AccountDialogType;
};

export type GenerateReportsAsyncPayload = {
  reportTypes: ReportType[];
  customerReferenceId: string | undefined;
  externalReference: string | undefined;
  refreshableItems: RefreshableItem[];
  credentialIds: string[];
};

interface ReportsServiceRedux {
  getAccounts: (payload: BundledFlowParameters) => ReportsThunk;
  generateReports: (payload: GenerateReportsPayload, idempotencyKey: string) => ReportsThunk;
  generateReportsAsync: (
    payload: GenerateReportsAsyncPayload,
    idempotencyKey: string
  ) => ReportsThunk;
}

export const ERR_REPORTS_FAILED_DESCRIBE = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_REPORTS_DESCRIBE,
};

export const ERR_REPORTS_FAILED_TO_GET_ACCOUNTS = {
  errorStatus: ErrorStatus.TEMPORARY_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_ACCOUNTS,
};

export const ERR_REPORTS_FAILED_GENERATE_REPORT = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CREATE_REPORT,
};

export type ReportsService = {
  getAccounts: (payload: BundledFlowParameters) => Promise<ReportAccount[]>;
  generateReports: (
    payload: GenerateReportsPayload,
    idempotencyKey: string
  ) => Promise<GeneratedReport[]>;
  generateReportsAsync: (
    payload: GenerateReportsAsyncPayload,
    idempotencyKey: string
  ) => Promise<GenerationJob>;
};

export const createReportsService = (client: ReportsClient) => {
  return {
    getAccounts: async (payload: BundledFlowParameters) => {
      const response = await client.getAccounts(payload);

      if (!response.isOk() || !response.data || response.data.accountDialogOptions.length === 0) {
        throw new ApplicationError(ERR_REPORTS_FAILED_TO_GET_ACCOUNTS);
      }

      return response.data.accountDialogOptions;
    },
    generateReports: async (payload: GenerateReportsPayload, idempotencyKey: string) => {
      const response = await client.generateReports(
        {
          accounts: payload.accounts,
          customerReferenceId: payload.customerReferenceId,
          requestedReportTypes: payload.reportTypes || [],
          requestedRefreshItems: payload.refreshableItems,
          requestedAccountDialogType: payload.accountDialogType,
          externalReference: payload.externalReference,
        },
        idempotencyKey
      );

      if (!response.isOk() || !response.data || !response.data.userReports) {
        throw new ApplicationError(ERR_REPORTS_FAILED_GENERATE_REPORT);
      }

      return response.data.userReports;
    },
    generateReportsAsync: async (payload: GenerateReportsAsyncPayload, idempotencyKey: string) => {
      const response = await client.generateReportsAsync(
        {
          customerReferenceId: payload.customerReferenceId,
          requestedReportTypes: payload.reportTypes || [],
          requestedRefreshItems: payload.refreshableItems,
          requestedAccountDialogType: AccountDialogType.NONE,
          credentialIds: payload.credentialIds,
          externalReference: payload.externalReference,
        },
        idempotencyKey
      );

      if (!response.isOk() || !response.data || !response.data.id) {
        throw new ApplicationError(ERR_REPORTS_FAILED_GENERATE_REPORT);
      }

      return response.data;
    },
  };
};

export const reportsService = createReportsService(reportsClient);

export function createReportsServiceWithDispatch(service: ReportsService): ReportsServiceRedux {
  return {
    getAccounts: (payload) => async (dispatch) => {
      try {
        const accountDialogOptions = await service.getAccounts(payload);
        dispatch(reportsSlice.actions.fetchReportAccountsSuccess(accountDialogOptions));
      } catch (e) {
        if (e instanceof ApplicationError) {
          dispatch(setApplicationError(e.commonError));
        } else {
          dispatch(setApplicationError(UNEXPECTED_ERROR));
        }
      }
    },
    generateReports:
      (payload: GenerateReportsPayload, idempotencyKey: string) => async (dispatch) => {
        try {
          const response = await service.generateReports(payload, idempotencyKey);
          dispatch(reportsSlice.actions.generateReportsSuccess(response));
        } catch (e) {
          if (e instanceof ApplicationError) {
            dispatch(setApplicationError(e.commonError));
          } else {
            dispatch(setApplicationError(UNEXPECTED_ERROR));
          }
        }
      },
    generateReportsAsync:
      (payload: GenerateReportsAsyncPayload, idempotencyKey: string) => async (dispatch) => {
        try {
          const response = await service.generateReportsAsync(payload, idempotencyKey);
          dispatch(reportsSlice.actions.generateReportsAsyncSuccess(response));
        } catch (e) {
          if (e instanceof ApplicationError) {
            dispatch(setApplicationError(e.commonError));
          } else {
            dispatch(setApplicationError(UNEXPECTED_ERROR));
          }
        }
      },
  };
}

export const reportsServiceRedux = createReportsServiceWithDispatch(reportsService);
